import React, { useState } from 'react';
import {
  Box,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfPlaceholder } from '@qualifio/ui-commons';
import { RuleType } from '@loyalty/shared-common/rule-api-client';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { useTranslation } from 'react-i18next';
import { DetailForm } from '../shared/layout/DetailForm';
import { EarningRuleFormValue } from './form';
import { EarningRuleModal } from './EarningRuleModal';
import { EarningRuleTableRow } from './EarningRuleTableRow';
import { QlfPlaceholderIcon } from '../shared/qlf-placeholder-icon/QlfPlaceholderIcon';
import { RuleConflictModal, RuleConflictModalProps } from '../rules/components/RuleConflictModal';
import { RuleImportExportButtons } from '../rules/components/import-export/RuleImportExportButtons';
import { RulesFilters } from '../rules/components';
import { earningRulesIcon } from '../shared/icons';
import { moveRule } from '../rules/utils';
import { ruleTableColumns } from '../rules/rule-table-constants';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useCurrencies } from '../currency/CurrenciesContext';
import { useFeatures } from '../features/FeaturesContext';
import { useProgram } from '../loyalty/ProgramContext';
import { useRules } from '../rules/RulesContext';

export const EMPTY_EARNING_RULES_CRITERIA: EarningRuleFormValue['criteria'] = {
  and: [],
};

export function EarningRulesTable() {
  const { earningRules: rules, fetchRules, setEarningRules, isLoading } = useRules();
  const { t } = useTranslation();
  const [conflictModalIsOpen, setConflictModalIsOpen] = useState(false);
  const [conflictDialogProps, setConflictDialogProps] = useState<
    Omit<RuleConflictModalProps, 'isOpen' | 'setIsOpen'>
  >({
    ruleType: RuleType.EARN,
  });
  const { currencies } = useCurrencies();
  const { features } = useFeatures();
  const { write: earningRuleWrite } = features[Feature.EARNING_RULE];
  const hasActiveCurrencies = !!currencies.find(currency => !currency.deletedAt);
  const emptyStateButtonTooltip = !earningRuleWrite
    ? t('shared.action.permission.required')
    : !hasActiveCurrencies
    ? t('rule.add.no-active-currency')
    : '';

  const [isModalOpen, setIsModalOpen] = useState(false);

  const refresh = () => fetchRules(RuleType.EARN);
  const { ruleApi } = useApi();
  const { programId } = useProgram();

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (destination && source) {
      const { newRules, changes } = moveRule(rules, source.index, destination.index);
      setEarningRules(newRules);
      ruleApi
        .updateRulesPosition(programId, changes)
        .then(refresh)
        .catch(error => {
          if (error.response?.status === 409) {
            setConflictDialogProps({
              ...conflictDialogProps,
              lastModification: error.response?.data?.details,
            });
            setConflictModalIsOpen(true);
          } else {
            throw error;
          }
        });
    }
  };

  return (
    <>
      {isLoading('fetchRules') && <Skeleton />}
      <RuleConflictModal
        {...conflictDialogProps}
        setIsOpen={setConflictModalIsOpen}
        isOpen={conflictModalIsOpen}
      />
      <EarningRuleModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
      {rules.length <= 0 ? (
        <QlfPlaceholder
          icon={<QlfPlaceholderIcon icon={earningRulesIcon} />}
          primaryText={t('earning-rules.messages.no-items')}
          secondaryText={t('earning-rules.states.empty.description')}
          actions={
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '20px',
              }}
            >
              <RuleImportExportButtons ruleType="EARNING_RULE" />
              <Tooltip title={emptyStateButtonTooltip} placement="bottom">
                <div>
                  <Button
                    disabled={!earningRuleWrite || !hasActiveCurrencies}
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => setIsModalOpen(true)}
                    id={'create-first-earning-rule-id'}
                  >
                    {t('earning-rules.states.empty.create-action')}
                  </Button>
                </div>
              </Tooltip>
            </Box>
          }
        />
      ) : (
        <DetailForm>
          <DetailForm.Header>
            <RulesFilters ruleType="EARNING_RULE" onAdd={() => setIsModalOpen(true)} />
          </DetailForm.Header>
          <DetailForm.Form>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: ruleTableColumns.drag.width }}></TableCell>
                  <TableCell>{t('earning-rules.row.name')}</TableCell>
                  <TableCell style={{ width: ruleTableColumns.source.width }}>
                    {t('earning-rules.row.source')}
                  </TableCell>
                  <TableCell style={{ width: ruleTableColumns.currencies.width }}>
                    {t('earning-rules.row.currencies')}
                  </TableCell>
                  <TableCell style={{ width: ruleTableColumns.actions.width }}></TableCell>
                </TableRow>
              </TableHead>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="earning-rules">
                  {({ placeholder, droppableProps, innerRef }) => (
                    <TableBody {...droppableProps} ref={innerRef}>
                      {rules.map((rule, index) => (
                        <Draggable key={rule.id} draggableId={rule.id} index={index}>
                          {({ innerRef, draggableProps, dragHandleProps }) => (
                            <EarningRuleTableRow
                              rule={rule}
                              onCloned={refresh}
                              onDeleted={refresh}
                              dragHandleProps={dragHandleProps}
                              draggableRef={innerRef}
                              draggableProps={draggableProps}
                            />
                          )}
                        </Draggable>
                      ))}
                      {placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
          </DetailForm.Form>
        </DetailForm>
      )}
    </>
  );
}
